/**
 * @fileoverview Connect validated identity and workspace availability to application navigation. `kc-internal-frontend/src/AppAccessContext.js` owns section choices and `kc-internal-frontend/src/AuthContext.js` owns session state; domain screens own business actions. Sidebar selection, scroll memory, and review badges are browser context. Logout crosses the session boundary explicitly rather than merely hiding navigation.
 */

import React, { useEffect, useLayoutEffect, useRef, useState, useContext } from "react";
import { Ripple } from "primereact/ripple";
import { useLocation, Link, useNavigate } from 'react-router-dom';
// Removed Divider import as we are simplifying the sidebar header area
import { TieredMenu } from 'primereact/tieredmenu';
import { AuthContext } from '../AuthContext';
import { useAppAccess } from '../AppAccessContext';
import "./SidebarLayout.css";
import logo from '../assets/img/Logo_1749325971610.png';
import api from "../api";
import Avatar from './Avatar/Avatar';
// import { useSelector, useDispatch } from 'react-redux'
import AppIcon from "./shared/AppIcon";
import FrontendUpdateNotice, { useFrontendUpdate } from "./FrontendUpdateNotice";
import { AssignedAttorneyReviewCountChip, useAssignedAttorneyReviewCount } from "./Intake/useAssignedAttorneyReviewCount";
import {
  getLastIntakeWorkspacePath,
  isIntakeWorkspacePath,
  rememberIntakeWorkspaceLocation,
} from "./Intake/intakeWorkspaceState";
import { isHistoricalDraftingTestingEnabled } from "../matterDraftingFeature";
import { isLocalFeatureProposalsEnabled, LOCAL_FEATURE_PROPOSALS_PATH } from "../localFeatureProposalsFeature";



function isDocumentsPath(pathname) {
  return pathname === "/documents" || pathname.startsWith("/documents/");
}



function isIntakePath(pathname) {
  return pathname === "/intake" || pathname.startsWith("/intake/");
}

function getRouteScrollKey({ pathname, search }) {
  if (/^\/case_details\/[^/]+\/?$/.test(pathname)) {
    // Tabs share the same matter page and its outer scroll position.
    const params = new URLSearchParams(search);
    params.delete('tab');
    const query = params.toString();
    return `${pathname}${query ? `?${query}` : ''}`;
  }
  return `${pathname}${search || ''}`;
}

const ACTIVE_LINK_CLASS = "kc-sidebar-link p-ripple flex align-items-center cursor-pointer p-3 border-round active";
const INACTIVE_LINK_CLASS = "kc-sidebar-link p-ripple flex align-items-center cursor-pointer p-3 border-round";



function SidebarNavLink({ to, active, icon, label, children }) {
  return (
    <li>
      <Link to={to} className={active ? ACTIVE_LINK_CLASS : INACTIVE_LINK_CLASS}>
        <AppIcon icon={`${icon} mr-2`} />
        <span className="font-medium">{label}</span>
        {children}
        <Ripple />
      </Link>
    </li>
  );
}



function SidebarSection({ id, label, children }) {
  if (React.Children.count(children) === 0) return null;
  return (
    <section className="kc-sidebar-section" aria-labelledby={id}>
      <h2 id={id} className="kc-sidebar-section-title">{label}</h2>
      <ul className="list-none p-0 m-0">{children}</ul>
    </section>
  );
}



function SidebarLayout({ children }) {
  const { clearAuthState } = useContext(AuthContext);
  const frontendUpdateAvailable = useFrontendUpdate();
  const { canAccess, canAccessLocation, homePath } = useAppAccess();
  let navigate = useNavigate();

  const navigationButtonRef = useRef(null);
  const sidebarRef = useRef(null);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const menu = useRef(null);
  const contentScrollRef = useRef(null);
  const routeScrollPositionsRef = useRef(new Map());
  const activeScrollKeyRef = useRef(null);
  const lastDocumentsPathRef = useRef("/documents");
  const lastIntakePathRef = useRef(getLastIntakeWorkspacePath());

  const location = useLocation();
  const routeScrollKey = getRouteScrollKey(location);
  const currentRoutePath = `${location.pathname}${location.search || ""}${location.hash || ""}`;
  const documentsRouteActive = isDocumentsPath(location.pathname);
  const documentsLinkPath = documentsRouteActive ? currentRoutePath : lastDocumentsPathRef.current;
  const intakeRouteActive = isIntakePath(location.pathname);
  const intakeWorkspaceRouteActive = isIntakeWorkspacePath(location.pathname);
  const intakeLinkPath = intakeWorkspaceRouteActive ? currentRoutePath : lastIntakePathRef.current;
  const historicalDraftingTestingEnabled = isHistoricalDraftingTestingEnabled();
  // LOCAL-ONLY: literal NODE_ENV check lets production builds drop the nav entry.
  const localFeatureProposalsEnabled = process.env.NODE_ENV === 'development' && isLocalFeatureProposalsEnabled();
  const [user, setUser] = useState(null);
  const { count: assignedReviewCount } = useAssignedAttorneyReviewCount(canAccess('intake'));
  // const dispatch = useDispatch()
  // const docChats = useSelector(state => state.app.docChats)

  useEffect(() => {
    let active = true;
    api.get('/user').then((response) => {
      if (active) setUser(response.data);
    }
    ).catch((error) => {
      if (active) console.error(error);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (documentsRouteActive) {
      lastDocumentsPathRef.current = currentRoutePath;
    }
  }, [currentRoutePath, documentsRouteActive]);

  useEffect(() => {
    if (intakeWorkspaceRouteActive) {
      lastIntakePathRef.current = rememberIntakeWorkspaceLocation(location);
    }
  }, [currentRoutePath, intakeWorkspaceRouteActive, location]);

  useLayoutEffect(

  () => {
    const scrollElement = contentScrollRef.current;
    if (!scrollElement) {
      return;
    }

    const previousScrollKey = activeScrollKeyRef.current;
    if (previousScrollKey && previousScrollKey !== routeScrollKey) {
      routeScrollPositionsRef.current.set(previousScrollKey, scrollElement.scrollTop);
    }

    activeScrollKeyRef.current = routeScrollKey;
    scrollElement.scrollTop = routeScrollPositionsRef.current.get(routeScrollKey) || 0;
  }, [routeScrollKey]);

  useEffect(

  () => {
    const scrollElement = contentScrollRef.current;
    if (!scrollElement) {
      return undefined;
    }

    const handleScroll =

    () => {
      const activeScrollKey = activeScrollKeyRef.current;
      if (activeScrollKey) {
        routeScrollPositionsRef.current.set(activeScrollKey, scrollElement.scrollTop);
      }
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileNavigationOpen(false); }, [currentRoutePath]);

  useEffect(

  () => {
    const media = window.matchMedia?.('(max-width: 1023px)');
    const closeOnResize =

    () => setMobileNavigationOpen(false);
    media?.addEventListener?.('change', closeOnResize);
    return () => media?.removeEventListener?.('change', closeOnResize);
  }, []);

  useEffect(

  () => {
    if (!mobileNavigationOpen) return undefined;
    const focusable =

    () => [...(sidebarRef.current?.querySelectorAll('a[href], button:not([disabled])') || [])];
    focusable()[0]?.focus();
    const handleKey =

    (event) => {
      if (event.key === 'Escape') {
        setMobileNavigationOpen(false);
        navigationButtonRef.current?.focus();
      }
      if (event.key === 'Tab') {
        const items = focusable();
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [mobileNavigationOpen]);

  const logout =

  () => {
    api.post('/logout').then((response) => {
      clearAuthState();
      navigate('/login');
    }
    ).catch((error) => {
      console.error(error);
    });
  };

  const workLinks = [
    { to: '/matters', active: location.pathname === '/' || location.pathname === '/matters', icon: 'pi pi-table', label: 'Matters' },
    { to: intakeLinkPath, active: intakeRouteActive, icon: 'pi pi-inbox', label: 'Intake', children: <AssignedAttorneyReviewCountChip count={assignedReviewCount} className="kc-sidebar-notification-chip" testId="sidebar-intake-review-count" /> },
    { to: '/inbox', active: location.pathname === '/inbox', icon: 'pi pi-check-square', label: 'Inbox' },
    { to: '/calendar', active: location.pathname.startsWith('/calendar'), icon: 'pi pi-calendar', label: 'Calendar' },
    { to: documentsLinkPath, active: documentsRouteActive, icon: 'pi pi-file', label: 'Documents' },
    { to: '/document_chat', active: location.pathname === '/document_chat', icon: 'pi pi-comments', label: 'Assistant' },
    { to: '/trello_tasks', active: location.pathname === '/trello_tasks', icon: 'pi pi-check-square', label: 'Trello Tasks' },
  ];
  const operationLinks = [
    ...(localFeatureProposalsEnabled ? [{ to: LOCAL_FEATURE_PROPOSALS_PATH, icon: 'pi pi-check-square', label: 'Feature proposals' }] : []),
    ...(historicalDraftingTestingEnabled ? [{ to: '/drafting-runs', icon: 'pi pi-sitemap', label: 'Drafting Runs' }] : []),
    { to: '/automations', icon: 'pi pi-bolt', label: 'Automations' },
    { to: '/utilities', icon: 'pi pi-wrench', label: 'Utilities' },
    { to: '/sync', icon: 'pi pi-sync', label: 'Sync' },
    { to: '/logs', icon: 'pi pi-list', label: 'Logs' },
    { to: '/settings', icon: 'pi pi-sliders-h', label: 'Settings' },
    ...(user?.IsAdmin ? [{ to: '/admin', icon: 'pi pi-cog', label: 'Admin' }] : []),
  ].map((item) => ({ ...item, active: location.pathname === item.to }));
  const availableLinks =
  (items) => items.filter(({ to }) => {
    const [pathname, search = ''] = to.split('?');
    return canAccessLocation({ pathname, search });
  }).map((item) => <SidebarNavLink key={item.label} {...item} />);

  return (
    <div className={`kc-app-shell${frontendUpdateAvailable ? " has-frontend-update" : ""}`}>
            <FrontendUpdateNotice available={frontendUpdateAvailable} />
            {mobileNavigationOpen && <button type="button" className="kc-sidebar-backdrop" aria-label="Dismiss navigation" onClick={() => { setMobileNavigationOpen(false); navigationButtonRef.current?.focus(); }} />}
            <div id="app-sidebar-10" ref={sidebarRef} className={`kc-sidebar${mobileNavigationOpen ? ' is-mobile-open' : ''}`} onClick={(event) => {
              if (event.target.closest('a[href]')) setMobileNavigationOpen(false);
            }}>
                <div className="flex flex-column h-full">
                    <div className="kc-sidebar-header flex align-items-center flex-shrink-0">
                        <button type="button" className="kc-sidebar-close" aria-label="Close navigation" onClick={() => { setMobileNavigationOpen(false); navigationButtonRef.current?.focus(); }}>
                          <AppIcon icon="pi pi-times" />
                        </button>
                        <Link to={homePath} className="kc-sidebar-brand">
                            <img src={logo} alt="Kneupper & Covey" className="kc-sidebar-brand-logo" />
                            <span className="kc-sidebar-brand-divider" aria-hidden="true"></span>
                            <span className="kc-sidebar-brand-label">Internal</span>
                        </Link>
                    </div>
                    <div className="overflow-y-auto">
                        <nav className="kc-sidebar-navigation" aria-label="Main navigation">
                            <SidebarSection id="sidebar-work-heading" label="Work">
                                {availableLinks(workLinks)}
                            </SidebarSection>
                            <SidebarSection id="sidebar-operations-heading" label="Operations">
                                {availableLinks(operationLinks)}
                            </SidebarSection>
                        </nav>
                        {user &&
            <div className="mt-auto">
                                <hr className="mb-3 mx-3 border-top-1 border-none surface-border" />
                                <TieredMenu model={[
              {
                label: 'Settings',
                icon: <AppIcon icon="pi pi-fw pi-cog" />,
                command: () => {navigate('/settings');}
              },
              {
                label: 'Logout',
                icon: <AppIcon icon="pi pi-fw pi-power-off" />,
                command: () => {logout();}
              }]}
              popup ref={menu} breakpoint="767px" />
                                <button type="button" className="kc-sidebar-user-button p-ripple m-3 flex align-items-center cursor-pointer p-3 text-700 border-2 border-transparent hover:border-300 transition-duration-150 transition-colors"
              style={{ borderRadius: '12px', background: 'transparent' }}
              onClick={(e) => menu.current.toggle(e)}>
                                    <Avatar
                  src={user.ProfilePic}
                  name={user.Name}
                  size={28}
                  className="mr-2" />

                                    <span className="font-medium">{user.Name}</span>
                                    <Ripple />
                                </button>
                            </div>}

                    </div>
                </div>
            </div>
            <div className="kc-app-main">
                    <header className="kc-mobile-shell-header">
                        <button type="button" ref={navigationButtonRef} className="kc-mobile-menu-button" aria-label="Open navigation" aria-controls="app-sidebar-10" aria-expanded={mobileNavigationOpen} onClick={() => setMobileNavigationOpen((open) => !open)}>
                            <AppIcon icon="pi pi-bars" weight="regular" />
                        </button>
                        <Link to={homePath} className="kc-sidebar-brand kc-sidebar-brand--compact">
                            <img src={logo} alt="Kneupper & Covey" className="kc-sidebar-brand-logo kc-sidebar-brand-logo--compact" />
                            <span className="kc-sidebar-brand-divider kc-sidebar-brand-divider--compact" aria-hidden="true"></span>
                            <span className="kc-sidebar-brand-label kc-sidebar-brand-label--compact">Internal</span>
                        </Link>
                    </header>
                    <main className="kc-content-panel" aria-label="Page content">
                        <div className="kc-content-scroll" ref={contentScrollRef}>
                            {children}
                        </div>
                    </main>
                </div>

        </div>);

}

export default SidebarLayout;
